import { RiBuilding2Line, RiDashboardLine, RiFileList3Line, RiLogoutBoxRLine } from 'react-icons/ri';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { clearJwtToken, decodeJwtPayload, getJwtToken } from '@/utils/self-hosted/jwt-manager';

const NAV = [
  { to: '/superadmin', label: 'Overview', icon: RiDashboardLine, end: true },
  { to: '/superadmin/tenants', label: 'Tenants', icon: RiBuilding2Line, end: false },
  { to: '/superadmin/audit', label: 'Audit log', icon: RiFileList3Line, end: false },
];

/**
 * Shell for the operator console.
 *
 * Deliberately NOT the tenant dashboard chrome: there is no environment
 * switcher and no workflow navigation, because a superadmin session is not
 * scoped to an environment and cannot act inside one. Keeping the two shells
 * separate is also what stops a half-initialised environment context from
 * rendering here.
 */
export function SuperAdminLayout() {
  const navigate = useNavigate();
  const claims = decodeJwtPayload<{ email?: string }>(getJwtToken());

  const signOut = () => {
    clearJwtToken();
    navigate('/auth/sign-in', { replace: true });
  };

  return (
    <div className="bg-bg-weak flex min-h-screen w-full">
      <aside className="border-stroke-soft bg-bg-white flex w-60 shrink-0 flex-col border-r">
        <div className="flex items-center gap-2 px-5 py-5">
          <img src="/images/logo.svg" alt="" className="size-6" onError={(e) => e.currentTarget.remove()} />
          <div className="flex flex-col">
            <span className="text-label-sm text-text-strong font-semibold">pingr-notify</span>
            <span className="text-paragraph-xs text-text-soft">Superadmin</span>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-label-sm transition-colors',
                  isActive ? 'bg-bg-weak text-text-strong font-medium' : 'text-text-sub hover:bg-bg-weak',
                ].join(' ')
              }
            >
              <item.icon className="size-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-stroke-soft flex flex-col gap-2 border-t px-3 py-4">
          {claims?.email && <span className="text-paragraph-xs text-text-soft truncate px-3">{claims.email}</span>}
          <button
            type="button"
            onClick={signOut}
            className="text-text-sub hover:bg-bg-weak flex items-center gap-2 rounded-lg px-3 py-2 text-label-sm transition-colors"
          >
            <RiLogoutBoxRLine className="size-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

/** Shared page frame so every console screen lines up. */
export function SuperAdminPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-8 py-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-title-h5 text-text-strong">{title}</h1>
          {description && <p className="text-paragraph-sm text-text-soft">{description}</p>}
        </div>
        {actions}
      </header>
      {children}
    </div>
  );
}
